import { CaretDown, Check } from "@phosphor-icons/react"
import * as SelectPrimitive from "@radix-ui/react-select"
import { cn } from "@/lib/utils"

export const Select = SelectPrimitive.Root

export const SelectValue = SelectPrimitive.Value

type SelectTriggerProps = React.ComponentPropsWithRef<typeof SelectPrimitive.Trigger> & { hasError?: boolean }
export const SelectTrigger = ({ className, children, hasError, ...props }: SelectTriggerProps) => (
  <SelectPrimitive.Trigger
    data-error={hasError}
    className={cn(
      "group bg-surface-subdued text-on-surface-subdued outline-primary hover:bg-surface-subdued-hovered focus:outline-primary disabled:hover:bg-surface-subdued! relative flex h-12 w-full items-center justify-between rounded-md border px-4 py-2 text-sm -outline-offset-2 transition-all duration-300 focus:outline-2 disabled:cursor-not-allowed",
      hasError && "outline-critical! outline-1 focus:outline-2!",
      className,
    )}
    {...props}>
    {children}
    <SelectPrimitive.Icon asChild>
      <CaretDown className="text-on-surface-subdued h-6 w-6" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
)

type SelectContentProps = React.ComponentPropsWithRef<typeof SelectPrimitive.Content>
export const SelectContent = ({ className, children, position = "popper", ...props }: SelectContentProps) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      className={cn(
        "bg-surface text-popover-foreground shadow-4 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 min-w-32 overflow-hidden rounded-md border",
        position === "popper" &&
          "data-[side=bottom]:translate-y-0 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className,
      )}
      position={position}
      {...props}>
      <SelectPrimitive.Viewport
        className={cn(
          "grid gap-1 p-2 has-data-radix-scroll-area-viewport:py-0",
          position === "popper" && "h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width)",
        )}>
        {children}
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
)

export const SelectLabel = ({ className, ...props }: React.ComponentPropsWithRef<typeof SelectPrimitive.Label>) => (
  <SelectPrimitive.Label className={cn("px-2 py-1.5 text-sm font-semibold", className)} {...props} />
)

export const SelectItem = ({
  className,
  children,
  ...props
}: React.ComponentPropsWithRef<typeof SelectPrimitive.Item>) => (
  <SelectPrimitive.Item
    className={cn(
      "data-[state='checked']:bg-primary-surface! data-disabled:text-on-surface-disabled data-[state='checked']:text-on-primary-surface! focus:bg-surface-hovered focus:text-on-surface relative flex w-full cursor-pointer items-center rounded-md py-1.5 pr-8 pl-2 text-sm transition-all duration-100 select-none focus-visible:outline-none data-disabled:pointer-events-none",
      className,
    )}
    {...props}>
    <span className="absolute top-1/2 right-2 flex h-3.5 w-3.5 -translate-y-1/2 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="text-on-primary-surface h-6 w-6" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
)

type AssistiveTextProps = React.HTMLAttributes<HTMLSpanElement> & { ref?: React.Ref<HTMLSpanElement> }
export const AssistiveText = ({ className, children, ...props }: AssistiveTextProps) => (
  <span className={cn("text-on-surface-subdued mt-1 ml-4 text-xs tracking-normal", className)} {...props}>
    {children}
  </span>
)

type LabelAndSelectValueProps = React.HTMLProps<HTMLDivElement> & {
  label: string
  value?: string
  required?: boolean
  ref?: React.Ref<HTMLDivElement>
}
export const LabelAndSelectValue = (props: LabelAndSelectValueProps) => {
  const { className, required, label, value, ...rest } = props
  return (
    <div {...rest}>
      <span
        className={cn(
          "text-on-surface-subdued group-focus:text-primary group-data-[state='open']:text-primary absolute top-3.5 right-12 left-4 truncate text-left text-sm transition-all",
          value && "group-data-[error='true']:text-critical top-1 text-xs leading-4 tracking-[0.25px]",
          className,
        )}>
        {label}
        {required && "*"}
      </span>
      {value && (
        <span title={value} className="text-on-surface absolute top-5.25 right-12 left-4 truncate text-left">
          {value}
        </span>
      )}
    </div>
  )
}
